// ============================================================================
// UI colour theme of the app shell ("Apariencia" in Personalización).
//
// Every account has always seen the same zBilling blue (#1a4f8b) and orange
// (#fca311). An account can now pick a theme — its own brand colours — and
// every user of that account gets it on login: the top bar, buttons, links,
// active tabs, switches and the light tints behind them. Documents / PDFs are
// NOT touched (they follow Diseño › Color and the template), and an account
// that never chooses keeps the classic look byte for byte: the default theme
// sets no attribute, so no override rule applies.
//
// How it works: the app's colours are Tailwind classes with literal values
// (text-[#1a4f8b], bg-blue-50, …). src/uiTheme.css re-points those classes to
// CSS variables, but only under `html[data-zb-theme]`; applyUiTheme() sets
// that attribute plus the variables of the chosen theme on <html>. The choice
// is stored with the rest of the account's design (profiles.document_design.
// ui_theme, see lib/documentDesign.js), so team members inherit the owner's.
// ============================================================================

export const DEFAULT_UI_THEME = 'zbilling';

// Variable names (without the --zb- prefix) every theme must define.
//   primary             fills: buttons, top-bar accents, active borders, rings
//   primary-text        the same colour as TEXT (a touch deeper to read well)
//   primary-hover       hover fill of primary buttons
//   primary-dark        deep variant (dark badges, gradient heads)
//   primary-light       lighter variant (gradient tails, focus borders)
//   tint                very light wash behind active items (was blue-50)
//   tint-strong         stronger wash (was blue-100)
//   tint-border         light border (was blue-200)
//   tint-border-strong  visible border (was blue-300)
//   accent              the second colour (was orange): avatar, badges, banners
//   accent-hover        hover fill of accent buttons
//   accent-text         accent as text on white
//   accent-tint         light wash behind accent text (was orange-100)
//   accent-tint-soft    softer wash (was orange-50)
export const THEME_VARS = [
  'primary', 'primary-text', 'primary-hover', 'primary-dark', 'primary-light',
  'tint', 'tint-strong', 'tint-border', 'tint-border-strong',
  'accent', 'accent-hover', 'accent-text', 'accent-tint', 'accent-tint-soft',
];

// `swatch` is what the picker shows: [primary, accent, tint]. Names are
// brand words, shown as they are in every language.
export const UI_THEMES = [
  {
    id: 'zbilling',
    name: 'zBilling',
    swatch: ['#1a4f8b', '#fca311', '#eff6ff'],
    // The classic look needs no overrides — see applyUiTheme().
    vars: null,
  },
  {
    id: 'egas',
    name: 'Cielo',
    swatch: ['#0b97d5', '#6fb22a', '#e8f6fd'],
    vars: {
      primary: '#0b97d5',
      'primary-text': '#0784bd',
      'primary-hover': '#0779ad',
      'primary-dark': '#075f88',
      'primary-light': '#3ab7ea',
      tint: '#e8f6fd',
      'tint-strong': '#cfeefb',
      'tint-border': '#a6dff6',
      'tint-border-strong': '#74cdf1',
      accent: '#6fb22a',
      'accent-hover': '#5c9722',
      'accent-text': '#4f8a1c',
      'accent-tint': '#e9f5d9',
      'accent-tint-soft': '#f3faea',
    },
  },
  {
    id: 'esmeralda',
    name: 'Esmeralda',
    swatch: ['#1f7a4d', '#f59e0b', '#e9f7ef'],
    vars: {
      primary: '#1f7a4d',
      'primary-text': '#1b6b44',
      'primary-hover': '#185f3c',
      'primary-dark': '#124a2f',
      'primary-light': '#2f9a65',
      tint: '#e9f7ef',
      'tint-strong': '#d1efdd',
      'tint-border': '#a9dfc0',
      'tint-border-strong': '#7fcda2',
      accent: '#f59e0b',
      'accent-hover': '#d97706',
      'accent-text': '#b45309',
      'accent-tint': '#fef3c7',
      'accent-tint-soft': '#fffbeb',
    },
  },
  {
    id: 'violeta',
    name: 'Violeta',
    swatch: ['#5b3fbf', '#e0567a', '#f1edfc'],
    vars: {
      primary: '#5b3fbf',
      'primary-text': '#4f35a8',
      'primary-hover': '#4a32a0',
      'primary-dark': '#3a2680',
      'primary-light': '#7c5fe0',
      tint: '#f1edfc',
      'tint-strong': '#e3dbf9',
      'tint-border': '#c8b9f2',
      'tint-border-strong': '#ab95eb',
      accent: '#e0567a',
      'accent-hover': '#c93f63',
      'accent-text': '#b8365a',
      'accent-tint': '#fce7ee',
      'accent-tint-soft': '#fef2f6',
    },
  },
  {
    id: 'grafito',
    name: 'Grafito',
    swatch: ['#2f3e4e', '#0d9488', '#eef1f4'],
    vars: {
      primary: '#2f3e4e',
      'primary-text': '#2a3846',
      'primary-hover': '#24303c',
      'primary-dark': '#1a232c',
      'primary-light': '#4a5c70',
      tint: '#eef1f4',
      'tint-strong': '#dfe4e9',
      'tint-border': '#c3ccd5',
      'tint-border-strong': '#a3b0bd',
      accent: '#0d9488',
      'accent-hover': '#0f766e',
      'accent-text': '#0f766e',
      'accent-tint': '#ccfbf1',
      'accent-tint-soft': '#f0fdfa',
    },
  },
  {
    id: 'coral',
    name: 'Coral',
    swatch: ['#c0392b', '#2c7fb8', '#fdecea'],
    vars: {
      primary: '#c0392b',
      'primary-text': '#b03427',
      'primary-hover': '#a93226',
      'primary-dark': '#7f241b',
      'primary-light': '#e0574a',
      tint: '#fdecea',
      'tint-strong': '#fad6d2',
      'tint-border': '#f4b1aa',
      'tint-border-strong': '#ec8a80',
      accent: '#2c7fb8',
      'accent-hover': '#256a9a',
      'accent-text': '#23608a',
      'accent-tint': '#e0effa',
      'accent-tint-soft': '#f0f7fd',
    },
  },
];

export function isUiTheme(id) {
  return UI_THEMES.some((t) => t.id === id);
}

// The theme for a stored id; anything unknown / missing is the classic look.
export function resolveUiTheme(id) {
  return UI_THEMES.find((t) => t.id === id) || UI_THEMES[0];
}

// The CSS custom properties a theme puts on <html>, as { '--zb-primary': … }.
// The default theme (vars: null) yields an empty object.
export function themeCssVars(id) {
  const th = resolveUiTheme(id);
  if (!th.vars) return {};
  return Object.fromEntries(THEME_VARS.map((k) => [`--zb-${k}`, th.vars[k]]));
}

// Apply a theme to the page: the attribute the override rules key on, plus
// the variables. `null`, an unknown id or the default theme removes everything
// so the classic look comes back (used on logout / when Layout unmounts).
export function applyUiTheme(id) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const th = resolveUiTheme(id);
  if (!th.vars) {
    root.removeAttribute('data-zb-theme');
    for (const k of THEME_VARS) root.style.removeProperty(`--zb-${k}`);
    return;
  }
  root.setAttribute('data-zb-theme', th.id);
  for (const [k, v] of Object.entries(themeCssVars(th.id))) root.style.setProperty(k, v);
}

export function currentUiTheme() {
  if (typeof document === 'undefined') return DEFAULT_UI_THEME;
  return document.documentElement.getAttribute('data-zb-theme') || DEFAULT_UI_THEME;
}

// Per-browser memory of the theme of the LAST account seen, keyed by user id,
// so the shell paints in the account's colours on the very first frame after
// login instead of flashing zBilling blue while the profile loads.
const CACHE_KEY = 'zb_ui_theme';
export function readCachedUiTheme(uid) {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    return raw && raw.uid === uid && isUiTheme(raw.theme) ? raw.theme : null;
  } catch { return null; }
}
export function writeCachedUiTheme(uid, theme) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ uid, theme })); } catch { /* ignore */ }
}
